import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import fp from 'lodash/fp';
import {
  toCurrency,
  color,
  media,
  mapState,
} from '@deerplusofficial/pos-helper';
import { Icon, Button } from '@deerplusofficial/pos-components';
import actions from '../../actions';
import withResetPos from '../../assist/withResetPos';
import Input from '../Input/Input';
import MemberModal from '../MemberModal/MemberModal';
import Tab from './Tab';
import CashTab from './CashTab';
import CreditCardTab from './CreditCardTab';
import PaymentsTab from './PaymentsTab';
import QrcodesTab from './QrcodesTab';
import EtcTab from './EtcTab';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 10px;
  background-color: #ffffff;
  ${media(
    'tablet',
    css`
      margin-left: 18px;
      padding: 18px;
      ${props =>
        props.narrow &&
        css`
          max-width: 660px;
          margin: 0 auto;
          align-self: flex-start;
        `};
    `
  )};
`;

const Hero = styled.div`
  padding: 25px;
  text-align: center;
  background-color: ${props =>
    props.lead ? color.accent : color.background.light};
  color: ${props => (props.lead ? '#ffffff' : color.text.title)};
  font-size: 30px;
`;

const Currency = styled.span`
  font-size: 18px;
`;

const OrderNumber = styled.div`
  font-size: 14px;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 7px 0 0;
  flex-shrink: 0;
`;

const TabIcon = styled(Icon)`
  margin-right: 0.5em;
`;

const BackButton = styled(Button).attrs({ primary: true })`
  margin-top: auto;
  ${media(
    'tablet',
    css`
      ${props =>
        props.leader &&
        css`
          margin-top: 120px;
        `};
    `
  )};
`;

const Confirmation = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  padding: 24px 0 4px;
  text-align: center;
  font-size: 24px;
  color: ${color.primary};
`;

const ConfirmationIcon = styled.span.attrs({
  className: 'fa-fw fa-layers fa-3x',
})`
  margin-bottom: 10px;
`;

const ReceiptButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  margin-top: 16px;
`;

const VATIdInput = styled(Input)`
  font-size: 20px;
  letter-spacing: 10px;
  padding: 10px;
  text-align: center;
  ${props =>
    fp.size(props.value) > 0 &&
    fp.size(props.value) < 8 &&
    css`
      border-color: ${color.error};
    `};
  ${props =>
    fp.size(props.value) === 8 &&
    css`
      border-color: ${color.accent};
    `};
`;

const tabs = [
  {
    name: 'cash',
    text: '現金',
    icon: 'money-bill-alt',
    predicate: { type: 1 },
    content: CashTab,
  },
  {
    name: 'card',
    text: '信用卡',
    icon: 'credit-card',
    predicate: { type: 2 },
    content: CreditCardTab,
  },
  {
    name: 'etc',
    text: '其他',
    icon: 'tags',
    predicate: { type: 3 },
    content: EtcTab,
  },
  {
    name: 'qrcode',
    text: '動態二元碼',
    icon: 'qrcode',
    predicate: { enable_qrcode: true },
    content: QrcodesTab,
  },
  {
    name: 'scanner',
    text: 'LinePay',
    icon: 'line',
    predicate: { enable_scanner: true },
    content: PaymentsTab,
    brand: true,
  },
];

class CheckoutPanel extends Component {
  static propTypes = {
    order: PropTypes.shape(),
  };

  static defaultProps = {
    order: {},
  };

  state = {
    activeTab: 'cash',
  };

  get isCheckoutEnd() {
    return this.props.orderStep === 'done';
  }

  get isInvoiceSubmitted() {
    return Boolean(_.size(this.props.invoice.status));
  }

  onTabToggle = activeTab => this.setState({ activeTab });

  onVatIdChange = e => {
    e.preventDefault();

    const { value } = e.target;

    if (fp.size(value) > 8 || /[^\d]/.test(value)) return;

    this.props.editVatId(value);
  };

  renderCheckoutMethods = () => {
    const { activeTab } = this.state;
    const { paymentMethods } = this.props.products;
    const filteredTabs = _.filter(tabs, ({ predicate }) => {
      return Boolean(_.size(_.filter(paymentMethods, predicate)));
    });
    const { predicate, content: TabContent } = _.find(filteredTabs, {
      name: activeTab,
    });

    return (
      <React.Fragment>
        <Tabs>
          {_.map(filteredTabs, ({ name, text, icon, brand }) => (
            <Tab
              key={name}
              name={name}
              activeTab={activeTab}
              onClick={this.onTabToggle}
            >
              <TabIcon icon={icon} brand={brand} size="lg" />
              {text}
            </Tab>
          ))}
        </Tabs>
        <TabContent meta={_.filter(paymentMethods, predicate)} />
      </React.Fragment>
    );
  };

  renderComplete = () => {
    const {
      resetPos,
      toggleInvoiceModal,
      invoice: { vatId, status },
    } = this.props;

    return (
      <React.Fragment>
        <Confirmation>
          <ConfirmationIcon>
            <Icon icon="circle" />
            <Icon icon="check" transform="shrink-6" />
          </ConfirmationIcon>
          交易完成
        </Confirmation>
        <VATIdInput
          placeholder="買受人統一編號"
          value={vatId}
          disabled={this.isInvoiceSubmitted}
          onChange={this.onVatIdChange}
        />
        <ReceiptButtons>
          <Button
            accent
            disabled={this.isInvoiceSubmitted}
            onClick={toggleInvoiceModal}
          >
            {this.isInvoiceSubmitted ? status : '開設電子發票'}
          </Button>
        </ReceiptButtons>
        <BackButton leader={this.isCheckoutEnd} onClick={resetPos}>
          回首頁
        </BackButton>
      </React.Fragment>
    );
  };

  render() {
    const {
      orderStep,
      order: { price, points },
      receipt: { orderNumber },
    } = this.props;

    return (
      <React.Fragment>
        <Container narrow={this.isCheckoutEnd}>
          <Hero lead={!this.isCheckoutEnd}>
            {toCurrency(price - points)}
            <Currency> TWD</Currency>
            {this.isCheckoutEnd && (
              <OrderNumber>訂單編號：{orderNumber}</OrderNumber>
            )}
          </Hero>
          {this.isCheckoutEnd && this.renderComplete()}
          {orderStep === 'checkout' && this.renderCheckoutMethods()}
        </Container>
        <MemberModal />
      </React.Fragment>
    );
  }
}

const {
  vatId: { edit: editVatId },
  modal: { toggle: toggleInvoiceModal },
} = actions.invoice;

export default fp.compose(
  withResetPos(),
  connect(
    mapState(['order', 'orderStep', 'receipt', 'invoice', 'products']),
    { editVatId, toggleInvoiceModal }
  )
)(CheckoutPanel);



// WEBPACK FOOTER //
// ./src/components/CheckoutPanel/CheckoutPanel.js